// Recorder — wraps rrweb.record(), buffers events, flushes them as
// $snapshot chunks via a separate ReplayTransport.
//
// rrweb is a peer dependency loaded lazily via dynamic import so apps
// that don't enable replay never see it in their bundle.
import { ReplayTransport } from './transport.js';
import type {
  ResolvedReplayConfig,
  RrwebRecordFn,
  RrwebRecordOptions,
  RrwebStopFn,
  SnapshotChunk
} from './types.js';

/** Time threshold for flushing buffered events (ms). */
const FLUSH_INTERVAL_MS = 5_000;
/** Event-count threshold for flushing buffered events. */
const FLUSH_EVENT_THRESHOLD = 50;

/** Allow tests to inject a fake rrweb without touching the real package. */
export interface RrwebProvider {
  load(): Promise<{ record: RrwebRecordFn }>;
}

/** Default provider — dynamic-imports rrweb at runtime. */
const defaultRrwebProvider: RrwebProvider = {
  load: async () => {
    // Dynamic import keeps rrweb out of the SDK bundle and lets
    // bundlers tree-shake it when not used. The string literal is
    // intentional — it's a peer dep declared in package.json.
    /* webpackChunkName: "rrweb" */
    const mod: unknown = await import(/* @vite-ignore */ 'rrweb');
    const m = mod as { record?: RrwebRecordFn; default?: { record?: RrwebRecordFn } };
    const record = m.record ?? m.default?.record;
    if (typeof record !== 'function') {
      throw new Error('rrweb is installed but does not export a `record` function');
    }
    return { record };
  }
};

export interface RecorderOptions {
  config: ResolvedReplayConfig;
  appId: string;
  sdkVersion: string;
  enableDebug?: boolean;
  /** Returns the current Tier-1 session ID. */
  getSessionId: () => string;
  /** Returns the current Tier-2 pageview ID, if available. */
  getPageviewId?: () => string | undefined;
  /**
   * Consent gate. Recorder calls this before start() and again after
   * any "consent changed" hint; if it returns false, recording is paused.
   */
  isConsentGranted: () => boolean;
  /** Optional rrweb provider override (used in tests). */
  rrwebProvider?: RrwebProvider;
  /** Optional UUID generator override (used in tests). */
  generateId?: () => string;
}

export class Recorder {
  private readonly config: ResolvedReplayConfig;
  private readonly appId: string;
  private readonly sdkVersion: string;
  private readonly enableDebug: boolean;
  private readonly getSessionId: () => string;
  private readonly getPageviewId?: () => string | undefined;
  private readonly isConsentGranted: () => boolean;
  private readonly rrwebProvider: RrwebProvider;
  private readonly generateId: () => string;

  private readonly transport: ReplayTransport;

  // rrweb state
  private rrwebStop: RrwebStopFn | null = null;
  private starting = false;

  // Sampling state
  private replayId: string | null = null;
  private sampledIn = false;

  // Buffer + flush state
  private buffer: unknown[] = [];
  private bufferBytes = 0;
  private chunkIndex = 0;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  // Lifecycle listeners we install ourselves
  private pageHideListener: (() => void) | null = null;

  // Public state
  private paused = false;
  private destroyed = false;

  // Track the consent state we last observed so we know when it transitions.
  private lastConsentGranted: boolean;

  constructor(options: RecorderOptions) {
    this.config = options.config;
    this.appId = options.appId;
    this.sdkVersion = options.sdkVersion;
    this.enableDebug = options.enableDebug ?? false;
    this.getSessionId = options.getSessionId;
    if (options.getPageviewId) this.getPageviewId = options.getPageviewId;
    this.isConsentGranted = options.isConsentGranted;
    this.rrwebProvider = options.rrwebProvider ?? defaultRrwebProvider;
    this.generateId = options.generateId ?? defaultGenerateId;

    this.transport = new ReplayTransport({
      endpoint: this.config.endpoint,
      appId: this.appId,
      sdkVersion: this.sdkVersion,
      enableDebug: this.enableDebug
    });

    this.lastConsentGranted = this.isConsentGranted();
  }

  /** Current replay ID, or null when not recording. */
  getReplayId(): string | null {
    return this.replayId;
  }

  /** Whether the recorder is actively producing rrweb events right now. */
  isRecording(): boolean {
    return this.rrwebStop !== null && !this.paused;
  }

  /**
   * Start recording (idempotent). Honors:
   * - sampling (decided once per call; if sampled out, returns without start)
   * - consent gate (skips start if denied)
   * - DOM availability (skips start if no `document`/`window`)
   */
  async start(): Promise<void> {
    if (this.destroyed) return;
    if (this.rrwebStop || this.starting) return;
    if (!this.config.enabled) return;
    if (typeof document === 'undefined' || typeof window === 'undefined') return;

    if (!this.isConsentGranted()) {
      if (this.enableDebug) {
        console.log('[Layers/replay] start skipped: analytics consent denied');
      }
      this.lastConsentGranted = false;
      return;
    }
    this.lastConsentGranted = true;

    // Sampling decision (once per replay session).
    if (!this.sampledIn) {
      this.sampledIn = Math.random() < this.config.sampleRate;
      if (!this.sampledIn) {
        if (this.enableDebug) {
          console.log(`[Layers/replay] sampled out (rate=${String(this.config.sampleRate)})`);
        }
        return;
      }
    }

    this.starting = true;
    let rrweb: { record: RrwebRecordFn };
    try {
      rrweb = await this.rrwebProvider.load();
    } catch (e) {
      this.starting = false;
      if (this.enableDebug) {
        console.warn(
          '[Layers/replay] rrweb not available — replay disabled. ' +
            'Install rrweb as a dependency to enable session replay.',
          e
        );
      }
      return;
    }
    this.starting = false;

    if (this.destroyed) return;
    if (this.rrwebStop) return; // started concurrently — bail

    this.replayId = this.replayId ?? this.generateId();
    this.chunkIndex = 0;
    this.buffer = [];
    this.bufferBytes = 0;

    const opts = this.buildRrwebOptions();
    try {
      this.rrwebStop = rrweb.record(opts);
    } catch (e) {
      if (this.enableDebug) {
        console.warn('[Layers/replay] rrweb.record threw:', e);
      }
      this.rrwebStop = null;
      return;
    }

    this.installPageHideListener();
    this.scheduleFlush();
  }

  /** Stop recording and flush any pending events. */
  stop(): void {
    this.removePageHideListener();
    this.cancelFlush();

    if (this.rrwebStop) {
      try {
        this.rrwebStop();
      } catch {
        // best effort
      }
      this.rrwebStop = null;
    }

    if (this.buffer.length > 0) {
      this.flushNow('stop');
    }
  }

  /** Pause without resetting replay ID — calls to `resume()` continue the same replay. */
  pause(): void {
    this.paused = true;
    if (this.rrwebStop) {
      try {
        this.rrwebStop();
      } catch {
        // best effort
      }
      this.rrwebStop = null;
    }
    if (this.buffer.length > 0) {
      this.flushNow('pause');
    }
  }

  /** Resume after pause. No-op if not paused or not previously sampled in. */
  async resume(): Promise<void> {
    if (!this.paused) return;
    this.paused = false;
    if (!this.sampledIn) return;
    if (!this.config.enabled) return;
    // Reuse same replayId, increment chunk index naturally.
    let rrweb: { record: RrwebRecordFn };
    try {
      rrweb = await this.rrwebProvider.load();
    } catch {
      return;
    }
    if (this.destroyed || this.rrwebStop) return;
    try {
      this.rrwebStop = rrweb.record(this.buildRrwebOptions());
    } catch {
      this.rrwebStop = null;
      return;
    }
    this.installPageHideListener();
    this.scheduleFlush();
  }

  /**
   * Notify the recorder that consent state may have changed. Starts the
   * recorder on Denied -> Granted, stops it on Granted -> Denied.
   */
  onConsentChange(): void {
    const granted = this.isConsentGranted();
    if (granted === this.lastConsentGranted) return;
    this.lastConsentGranted = granted;
    if (granted) {
      void this.start();
    } else {
      // Stop and discard any buffered + in-flight chunks — consent retraction.
      this.stop();
      this.buffer = [];
      this.bufferBytes = 0;
      this.transport.reset();
    }
  }

  /** Tear down — stop recording, drop pending state, remove all listeners. */
  destroy(): void {
    this.destroyed = true;
    this.stop();
    this.transport.reset();
    this.replayId = null;
    this.sampledIn = false;
  }

  /** Flush any buffered events synchronously via beacon (page-hide path). */
  flushOnPageHide(): void {
    if (this.buffer.length > 0) {
      this.flushNow('pagehide');
    }
    this.transport.flushBeacon();
  }

  // --- internals -------------------------------------------------------

  private buildRrwebOptions(): RrwebRecordOptions {
    return {
      emit: (event: unknown) => {
        this.handleRrwebEvent(event);
      },
      maskAllInputs: this.config.maskAllInputs,
      maskAllText: this.config.maskAllText,
      maskTextSelector: this.config.maskTextSelector,
      blockSelector: this.config.blockSelector,
      recordCanvas: this.config.recordCanvas,
      recordCrossOriginIframes: this.config.recordCrossOriginIframes
    };
  }

  private handleRrwebEvent(event: unknown): void {
    if (this.paused || this.destroyed) return;
    this.buffer.push(event);
    // Approximate size — we re-serialize on flush, so this is just a hint.
    try {
      this.bufferBytes += JSON.stringify(event).length;
    } catch {
      this.bufferBytes += 256;
    }
    if (this.buffer.length >= FLUSH_EVENT_THRESHOLD) {
      this.flushNow('threshold');
    }
  }

  private scheduleFlush(): void {
    this.cancelFlush();
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      if (this.buffer.length > 0) {
        this.flushNow('interval');
      }
      // Re-arm only while recording continues.
      if (this.isRecording()) {
        this.scheduleFlush();
      }
    }, FLUSH_INTERVAL_MS);
  }

  private cancelFlush(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  private flushNow(reason: string): void {
    if (this.buffer.length === 0) return;
    if (!this.replayId) return;
    const eventsCount = this.buffer.length;
    let serialized: string;
    try {
      serialized = JSON.stringify(this.buffer);
    } catch (e) {
      if (this.enableDebug) {
        console.warn('[Layers/replay] flush JSON.stringify failed:', e);
      }
      this.buffer = [];
      this.bufferBytes = 0;
      return;
    }
    const pageviewId = this.getPageviewId?.();
    const chunk: SnapshotChunk = {
      replay_id: this.replayId,
      session_id: this.getSessionId(),
      ...(pageviewId && { pageview_id: pageviewId }),
      chunk_index: this.chunkIndex,
      events: serialized,
      events_count: eventsCount,
      size_bytes: serialized.length
    };
    this.chunkIndex += 1;
    this.buffer = [];
    this.bufferBytes = 0;
    if (this.enableDebug) {
      console.log(
        `[Layers/replay] flush(${reason}): chunk=${String(chunk.chunk_index)} ` +
          `events=${String(eventsCount)} bytes=${String(chunk.size_bytes)}`
      );
    }
    void this.transport.send(chunk);
  }

  private installPageHideListener(): void {
    if (this.pageHideListener) return;
    if (typeof window === 'undefined') return;
    this.pageHideListener = () => {
      this.flushOnPageHide();
    };
    // `pagehide` fires reliably across navigations and tab close in modern browsers.
    window.addEventListener('pagehide', this.pageHideListener);
    // Also listen on visibilitychange -> hidden as a belt-and-suspenders.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  private removePageHideListener(): void {
    if (this.pageHideListener && typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.pageHideListener);
    }
    this.pageHideListener = null;
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  private onVisibilityChange = (): void => {
    if (typeof document === 'undefined') return;
    if (document.visibilityState === 'hidden') {
      this.flushOnPageHide();
    }
  };

  // Test-only: peek at the transport for assertions.
  /** @internal */
  _getTransport(): ReplayTransport {
    return this.transport;
  }
}

function defaultGenerateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r % 4) + 8;
    return v.toString(16);
  });
}
